import React, { useState } from "react";
import {Badge} from '@/common/ui/badge';
import {AlertTriangle, Star, Clock} from "lucide-react";
import { StyledTrashButton } from '@/common/components/button/StyledButton';
import { ChildHeader as TableHeader, ChildHeaderCell as HeaderCell, ChildTable as Table, ChildBody as TableBody, ChildRow as TableRow, ChildCell as TableCell } from '@/common/components/TableChild';
import LoadingSpinner from '@/common/components/LoadingSpinner';
import FocusPlanDeleteConfirm from './FocusPlanDeleteConfirm';
import { useNavigate } from 'react-router-dom';
  
interface PlannerTableProps {
  items: any[];
  isLoading?: boolean;
  // identifier can be a task_id (string|number) or an object like { recommendation_template_id, task_index } or { pillar_key, task_index }
  onRemove: (identifier: string | number | object) => void;
  onUpdateItem?: (identifier: string | number, updates: Partial<any>) => void;
  hasExistingActionPlans?: boolean;
  isMostRecent?: boolean;
} 

const PlannerTableComponent: React.FC<PlannerTableProps> = ({
  items,
  isLoading = false,
  onRemove,
  isMostRecent = true,
}) => {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingItem, setPendingItem] = useState<{ itemId: string | number; title?: string } | null>(null);
  const [pendingScrollY, setPendingScrollY] = useState<number | null>(null);

  const handleConfirmDelete = () => {
    const scrollY = pendingScrollY;
    if (pendingItem) {
      onRemove(pendingItem.itemId as any);
    }
    setConfirmOpen(false);
    setPendingItem(null);
    setPendingScrollY(null);

    setTimeout(() => {
      if (scrollY !== null) window.scrollTo(0, scrollY);
    }, 0);
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setPendingItem(null);
    setPendingScrollY(null);
  }; 

  return (
    <>
      <Table selectable>
        <TableHeader>
          <tr>
            <HeaderCell width={5}>Focus Area</HeaderCell>
            <HeaderCell width={6}>Description</HeaderCell>
            <HeaderCell width={4}>Soft Skills</HeaderCell>
            <HeaderCell width={1}>Action</HeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={4} className="p-12 text-center text-gray-500">
                <LoadingSpinner />
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="p-12 text-center text-gray-500">
                <div className="flex items-center justify-center gap-4">
                  {isMostRecent ? (
                    <>
                      <Star className="w-12 h-12 text-emerald-500 flex-shrink-0" />
                      <div className="flex flex-col items-start gap-1 pt-15 pb-15">
                        <span className="text-base">Start Your Focus Plan</span>
                        <span className="text-sm text-gray-400">
                          Click{' '}
                          <button
                            type="button"
                            className="text-gray-400 underline hover:text-blue-800"
                            onClick={() => navigate('/app/culture-hub/interventions/focus-plan-recommendations')}
                          >
                            Add to Focus Plan
                          </button>{' '}
                          to explore recommendations and add focus areas tailored to your team.
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <Clock className="w-12 h-12 text-gray-400 flex-shrink-0" />
                      <div className="flex flex-col items-start gap-1 pt-15 pb-15">
                        <span className="text-base">No focus plan for this period</span>
                        <span className="text-sm text-gray-400">No focus plan was created for this assessment period.</span>
                      </div>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ) : null}
          {items.map((it, i)=> {
            // Use task_id (from database) for saved items, fall back to recommendation_template_id for new items
            const itemId = it.task_id ?? it.recommendation_template_id ?? `item-${i}`;

            return (
            <TableRow
              key={itemId}
              role="button"
              tabIndex={0}
              $selectable
              onKeyDown={(e: { key: string; preventDefault: () => void; }) => { if (e.key === 'Enter') { e.preventDefault(); navigate(`/app/culture-hub/interventions/recommendation/${it.recommendation_template_id ?? 'unknown'}/${it.task_index}`, { state: { item: it } }); } }}
              onClick={() => navigate(`/app/culture-hub/interventions/recommendation/${it.recommendation_template_id ?? 'unknown'}/${it.task_index}`, { state: { item: it } })}
              className={`${!it.recommendation_template_id ? 'bg-yellow-50' : ''}`}
            >
              <TableCell $firstcol>
                <div className="flex-col-gap-2">
                  <div className="flex items-center gap-3">
                    <Star className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <div className="flex-center-gap">
                      {it.task || it.task_title || it.title}
                      {!it.recommendation_template_id && (
                        <Badge variant="outline" className="badge-status-in-progress">
                          <AlertTriangle className="icon-xs" />
                          No template
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </TableCell>

              <TableCell>{it.task_description || it.description || 'Not specified'}</TableCell>

              <TableCell>{it.softSkills || it.softSkills === '' ? (it.softSkills || 'Not specified') : 'Not specified'}</TableCell>

              <TableCell justify="center">
                <StyledTrashButton
                  id={itemId}
                  onClick={(maybeEventOrId: any) => {
                    // stopPropagation only if a real Event was passed
                    if (maybeEventOrId && typeof maybeEventOrId.stopPropagation === 'function') {
                      maybeEventOrId.stopPropagation();
                    }

                    // Open the confirmation modal instead of deleting immediately
                    setPendingItem({ itemId, title: it.task || it.task_title || it.title });
                    setPendingScrollY(window.scrollY);
                    setConfirmOpen(true);
                  }}
                  title="Remove"
                />
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <FocusPlanDeleteConfirm
        open={confirmOpen}
        itemTitle={pendingItem?.title}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
      {items.some(it => !it.recommendation_template_id) && (
        <div className="border-t border-gray-200 p-4 bg-white">
          <div className="text-xs text-yellow-700 flex items-center gap-1">
            <AlertTriangle className="icon-xs" />
            {items.filter(it => !it.recommendation_template_id).length} recommendation{items.filter(it => !it.recommendation_template_id).length !== 1 ? 's' : ''} without templates will be skipped
          </div>
        </div>
      )}
    </>
  );
};

export const PlannerTable = React.memo(PlannerTableComponent);
